import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/UnifiedAuthContext';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import { FaSave, FaUndo, FaUser, FaComments, FaPalette, FaTimes, FaArrowLeft } from 'react-icons/fa';
import { techniciansAPI } from '../../utils/api';
import './PersonaBuilder.scss';

const AVAILABLE_TRAITS = [
    'Professional',
    'Friendly',
    'Reliable',
    'Courteous',
    'Helpful',
    'Approachable',
    'Respectful',
    'Attentive',
    'Knowledgeable',
    'Punctual'
];

const PersonaBuilder = () => {
    const { technician } = useAuth();
    const navigate = useNavigate();
    const [personaForm, setPersonaForm] = useState({
        traits: ['', '', ''],
        personality: ['', ''],
        communicationStyle: ['', ''],
        tone: '',
        doNots: ['', '']
    });
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        if (technician?.persona) {
            loadPersonaData(technician.persona);
        }
    }, [technician]);

    const loadPersonaData = (persona) => {
        setPersonaForm({
            traits: Array.isArray(persona.traits)
                ? [...persona.traits, '', '', ''].slice(0, 3)
                : ['', '', ''],
            personality: Array.isArray(persona.personality)
                ? [...persona.personality, '', ''].slice(0, 2)
                : typeof persona.personality === 'string'
                ? [persona.personality, '']
                : ['', ''],
            communicationStyle: Array.isArray(persona.communicationStyle)
                ? [...persona.communicationStyle, '', ''].slice(0, 2)
                : typeof persona.communicationStyle === 'string'
                ? [persona.communicationStyle, '']
                : ['', ''],
            tone: persona.tone || '',
            doNots: Array.isArray(persona.doNots)
                ? [...persona.doNots, '', ''].slice(0, 2)
                : ['', '']
        });
    };

    const handleInputChange = (field, index, value) => {
        setPersonaForm(prev => {
            const newForm = { ...prev };
            if (index !== undefined) {
                newForm[field] = [...prev[field]];
                newForm[field][index] = value;
            } else {
                newForm[field] = value;
            }
            return newForm;
        });
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setMessage({ type: '', text: '' });

            // Clean and format persona data to match ejento-elevate structure
            const cleanedPersona = {
                traits: personaForm.traits.filter(trait => trait.trim() !== ''),
                personality: personaForm.personality.filter(p => p.trim() !== '').join(' and '),
                communicationStyle: personaForm.communicationStyle.filter(cs => cs.trim() !== '').join(' and '),
                tone: personaForm.tone.trim(),
                doNots: personaForm.doNots.filter(dont => dont.trim() !== '')
            };

            await techniciansAPI.updatePersona(technician.crmCode, cleanedPersona);
            setMessage({ type: 'success', text: 'Persona updated successfully!' });

            // Redirect to dashboard after successful save
            setTimeout(() => {
                navigate(`/${window.location.search}`);
            }, 1000);
        } catch (error) {
            console.error('Error saving persona:', error);
            setMessage({ type: 'error', text: 'Failed to save persona. Please try again.' });
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        navigate(`/${window.location.search}`);
    };

    const handleReset = () => {
        if (technician?.persona) {
            loadPersonaData(technician.persona);
        } else {
            setPersonaForm({
                traits: ['', '', ''],
                personality: ['', ''],
                communicationStyle: ['', ''],
                tone: '',
                doNots: ['', '']
            });
        }
        setMessage({ type: '', text: '' });
    };

    if (!technician) {
        return <LoadingSpinner />;
    }

    return (
        <div className="persona-builder-page">
            <div className="persona-builder">
                <div className="persona-builder__header">
                    <div className="persona-builder__title">
                        <FaUser className="persona-builder__icon" />
                        <div>
                            <h1>Edit Your Persona</h1>
                            <p>Customize how your responses sound to customers</p>
                        </div>
                    </div>
                    <div className="persona-builder__technician">
                        <strong>{technician.name}</strong>
                        <span className="technician-code">({technician.crmCode})</span>
                    </div>
                </div>

                {message.text && (
                    <div className={`persona-builder__message persona-builder__message--${message.type}`}>
                        {message.text}
                    </div>
                )}

                <div className="persona-builder__content">
                    <div className="persona-builder__section">
                        <div className="section-header">
                            <FaPalette className="section-icon" />
                            <h3>Personality Traits</h3>
                        </div>
                        <p className="section-description">
                            Select 3 key traits that describe your professional personality
                        </p>
                        <div className="input-group">
                            {personaForm.traits.map((trait, index) => (
                                <select
                                    key={index}
                                    className="persona-input"
                                    value={trait}
                                    onChange={(e) => handleInputChange('traits', index, e.target.value)}
                                >
                                    <option value="" disabled>Select Trait {index + 1}</option>
                                    {AVAILABLE_TRAITS.map(availableTrait => (
                                        <option
                                            key={availableTrait}
                                            value={availableTrait}
                                            disabled={personaForm.traits.includes(availableTrait) && trait !== availableTrait}
                                        >
                                            {availableTrait}
                                        </option>
                                    ))}
                                </select>
                            ))}
                        </div>
                    </div>

                    <div className="persona-builder__section">
                        <div className="section-header">
                            <FaUser className="section-icon" />
                            <h3>Personality Descriptors</h3>
                        </div>
                        <p className="section-description">
                            Describe your overall personality approach (up to 2 descriptors)
                        </p>
                        <div className="input-group">
                            {personaForm.personality.map((personality, index) => (
                                <input
                                    key={index}
                                    type="text"
                                    className="persona-input"
                                    placeholder={`Personality ${index + 1} (e.g., Customer-focused, Reliable)`}
                                    value={personality}
                                    onChange={(e) => handleInputChange('personality', index, e.target.value)}
                                    maxLength={50}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="persona-builder__section">
                        <div className="section-header">
                            <FaComments className="section-icon" />
                            <h3>Communication Style</h3>
                        </div>
                        <p className="section-description">
                            How do you prefer to communicate with customers? (up to 2 styles)
                        </p>
                        <div className="input-group">
                            {personaForm.communicationStyle.map((style, index) => (
                                <input
                                    key={index}
                                    type="text"
                                    className="persona-input"
                                    placeholder={`Style ${index + 1} (e.g., Direct and clear, Warm and personal)`}
                                    value={style}
                                    onChange={(e) => handleInputChange('communicationStyle', index, e.target.value)}
                                    maxLength={50}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="persona-builder__section">
                        <div className="section-header">
                            <FaComments className="section-icon" />
                            <h3>Message Tone</h3>
                        </div>
                        <p className="section-description">
                            What tone should your messages have?
                        </p>
                        <div className="input-group">
                            <input
                                type="text"
                                className="persona-input"
                                placeholder="e.g., Conversational, Professional, Friendly"
                                value={personaForm.tone}
                                onChange={(e) => handleInputChange('tone', undefined, e.target.value)}
                                maxLength={50}
                            />
                        </div>
                    </div>

                    <div className="persona-builder__section">
                        <div className="section-header">
                            <FaTimes className="section-icon" />
                            <h3>Things to Avoid</h3>
                        </div>
                        <p className="section-description">
                            What should your responses NOT do or say? (up to 2 items)
                        </p>
                        <div className="input-group">
                            {personaForm.doNots.map((doNot, index) => (
                                <input
                                    key={index}
                                    type="text"
                                    className="persona-input"
                                    placeholder={`Avoid ${index + 1} (e.g., Being too formal, Using technical jargon)`}
                                    value={doNot}
                                    onChange={(e) => handleInputChange('doNots', index, e.target.value)}
                                    maxLength={100}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="persona-builder__actions">
                        <button
                            className="btn btn--secondary"
                            onClick={handleCancel}
                            disabled={saving}
                        >
                            <FaArrowLeft /> Back
                        </button>
                        <button
                            className="btn btn--secondary"
                            onClick={handleReset}
                            disabled={saving}
                        >
                            <FaUndo /> Reset
                        </button>
                        <button
                            className="btn btn--primary"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? <LoadingSpinner size="small" /> : <FaSave />}
                            {saving ? 'Saving...' : 'Save Persona'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PersonaBuilder;
